import { INK } from './palette.js';

/**
 * The fifty collectibles, drawn.
 *
 * Every one of them was invented in the seed file - Thunder Wombats, The Night
 * Parrot, The Golden Boot - and none had ever been drawn. All fifty rendered as
 * one of five coloured circles by rarity, so a completed set was eight
 * identical stars and the legendary looked exactly like every other legendary.
 *
 * Six archetypes cover the lot, rather than fifty bespoke drawings. That is not
 * only less code: a collection has to LOOK like a collection. Fifty separately
 * drawn characters would drift in line weight, eye size and proportion, and a
 * shelf of near-misses reads as worse than a shelf of consistent ones. Each
 * character is the archetype plus its own colours and features.
 *
 * Original work throughout. The footy teams are invented and their mascots are
 * animals that actually live in Queensland; nothing here borrows a real club,
 * a real logo, or anybody else's character.
 */

/**
 * Painted art, where it exists.
 *
 * Vite resolves this glob at build time, so a new character is live the moment
 * its .webp lands in this folder - no list to update and no chance of a file
 * that ships but is never referenced. Anything without a painting falls through
 * to the drawn version below, which means the shelf is never broken while the
 * set is being filled in.
 */
const PAINTED = import.meta.glob<{ default: string }>('./collectibles/*.webp', {
  eager: true,
});

const PAINTED_BY_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(PAINTED).map(([path, mod]) => [
    path.replace('./collectibles/', '').replace('.webp', ''),
    mod.default,
  ]),
);

export type ArtKind = 'critter' | 'bird' | 'dino' | 'sea' | 'star' | 'object';

interface Spec {
  art: ArtKind;
  body: string;
  belly?: string;
  accent: string;
  ears?: 'round' | 'pointy' | 'tiny' | 'none';
  tail?: 'bushy' | 'thin' | 'flat' | 'none';
  /** Quills for an echidna, plates for a stegosaur. */
  spikes?: number;
  crest?: boolean;
  neck?: 'short' | 'long';
  stripes?: number;
  wings?: boolean;
  /** Sea shapes differ enough to be worth naming. */
  shape?: 'fish' | 'long' | 'blob' | 'flat' | 'shell' | 'huge';
  object?:
    | 'boot'
    | 'egg'
    | 'map-left'
    | 'map-right'
    | 'lantern'
    | 'key'
    | 'treehouse'
    | 'campfire'
    | 'rockpool'
    | 'observatory'
    | 'note';
  /** A soft halo. Reserved for legendaries, which should feel different. */
  glow?: boolean;
}

// --- the cast --------------------------------------------------------------

const SPECS: Record<string, Spec> = {
  // Footy Legends - invented clubs, Queensland animals.
  'fl-thunder-wombat': {
    art: 'critter',
    body: '#7d6552',
    belly: '#a08a75',
    accent: '#f2b134',
    ears: 'round',
    tail: 'none',
  },
  'fl-reef-shark': {
    art: 'sea',
    body: '#5d8aa8',
    belly: '#dfe7ec',
    accent: '#2e5d78',
    shape: 'fish',
  },
  'fl-outback-emu': { art: 'bird', body: '#6b5b47', accent: '#c25a26', neck: 'long' },
  'fl-mountain-quoll': {
    art: 'critter',
    body: '#8a5636',
    belly: '#c9a887',
    accent: '#f2f4f8',
    ears: 'round',
    tail: 'bushy',
    stripes: 5,
  },
  'fl-river-platypus': {
    art: 'critter',
    body: '#5a4632',
    belly: '#8d7355',
    accent: '#3f8f7d',
    ears: 'none',
    tail: 'flat',
  },
  'fl-desert-dingo': {
    art: 'critter',
    body: '#d99f74',
    belly: '#f6dcc0',
    accent: '#b0432a',
    ears: 'pointy',
    tail: 'bushy',
  },
  'fl-storm-cockatoo': { art: 'bird', body: '#3a3f4a', accent: '#f2b134', crest: true },
  'fl-golden-boot': {
    art: 'object',
    body: '#e8b530',
    accent: '#8a6a12',
    object: 'boot',
    glow: true,
  },

  // Dino Squad
  'ds-rumble': { art: 'dino', body: '#5f9e6e', belly: '#c7e0c9', accent: '#38684a' },
  'ds-spike': { art: 'dino', body: '#b05a3c', belly: '#e6bfa8', accent: '#7a3823', spikes: 4 },
  'ds-stomper': { art: 'dino', body: '#6c7a9c', belly: '#c6cfe0', accent: '#3f4b66' },
  'ds-pebble': { art: 'dino', body: '#9a8b6f', belly: '#ded4bd', accent: '#6a5d45' },
  'ds-glider': { art: 'dino', body: '#7b6bb5', belly: '#d3cbe8', accent: '#4b3f7a', wings: true },
  'ds-longneck': {
    art: 'dino',
    body: '#4f8f8a',
    belly: '#bcdedb',
    accent: '#2f605c',
    neck: 'long',
  },
  'ds-thunderfoot': {
    art: 'dino',
    body: '#8a4a5e',
    belly: '#dcb3c1',
    accent: '#5c2c3b',
    spikes: 3,
  },
  'ds-first-egg': { art: 'object', body: '#f3e6c8', accent: '#c2a05a', object: 'egg', glow: true },

  // Aussie Animals
  'aa-quokka': {
    art: 'critter',
    body: '#a9784f',
    belly: '#e0c3a2',
    accent: '#c25a26',
    ears: 'round',
    tail: 'thin',
  },
  'aa-wombat': {
    art: 'critter',
    body: '#8b7360',
    belly: '#b7a291',
    accent: '#5b4a3c',
    ears: 'round',
    tail: 'none',
  },
  'aa-bilby': {
    art: 'critter',
    body: '#b8b0c4',
    belly: '#e6e2ee',
    accent: '#5c5470',
    ears: 'pointy',
    tail: 'thin',
  },
  'aa-turtle': { art: 'sea', body: '#5f8f57', belly: '#cfe3c6', accent: '#3c5f36', shape: 'shell' },
  'aa-echidna': {
    art: 'critter',
    body: '#6d5847',
    belly: '#a08a75',
    accent: '#3a2f26',
    ears: 'tiny',
    tail: 'none',
    spikes: 7,
  },
  'aa-numbat': {
    art: 'critter',
    body: '#b5612e',
    belly: '#e4b58c',
    accent: '#f6f2ea',
    ears: 'pointy',
    tail: 'bushy',
    stripes: 6,
  },
  'aa-cassowary': { art: 'bird', body: '#2f3a44', accent: '#2e8fa8', crest: true, neck: 'long' },
  'aa-night-parrot': { art: 'bird', body: '#4f7a3a', accent: '#e8d44a', crest: false, glow: true },

  // Star Poppers - invented music sprites.
  'sp-melody': { art: 'star', body: '#f0a8c0', accent: '#8c3f5c', object: 'note' },
  'sp-harmony': { art: 'star', body: '#9fc8ea', accent: '#33587c', object: 'note' },
  'sp-beat': { art: 'star', body: '#f2c14e', accent: '#8a6512', object: 'note' },
  'sp-echo': { art: 'star', body: '#b6a8e0', accent: '#4d3f7a', object: 'note' },
  'sp-shimmer': { art: 'star', body: '#7fd8c8', accent: '#2c6b60', object: 'note' },
  'sp-riff': { art: 'star', body: '#f09a6a', accent: '#8f4a22', object: 'note' },
  'sp-encore': { art: 'star', body: '#e46a8a', accent: '#7a2440', object: 'note' },
  'sp-first-song': { art: 'star', body: '#f6e7a8', accent: '#a8862c', object: 'note', glow: true },

  // Ocean Deep
  'od-clownfish': {
    art: 'sea',
    body: '#ee8232',
    belly: '#f6f2ea',
    accent: '#2a2e2d',
    shape: 'fish',
    stripes: 3,
  },
  'od-seahorse': {
    art: 'sea',
    body: '#f2b134',
    belly: '#f7d489',
    accent: '#8a6512',
    shape: 'long',
  },
  'od-octopus': { art: 'sea', body: '#b25a9c', belly: '#e0b3d6', accent: '#6b2f5c', shape: 'blob' },
  'od-ray': { art: 'sea', body: '#6b84a8', belly: '#cdd8e6', accent: '#3a4a63', shape: 'flat' },
  'od-dolphin': { art: 'sea', body: '#7fa8c8', belly: '#e2edf4', accent: '#3f6480', shape: 'fish' },
  'od-turtle': { art: 'sea', body: '#4f7d5a', belly: '#c3ddc7', accent: '#2f4d36', shape: 'shell' },
  'od-whale': { art: 'sea', body: '#5a6f96', belly: '#c9d4e6', accent: '#33415c', shape: 'huge' },
  'od-lantern': {
    art: 'sea',
    body: '#3a4257',
    belly: '#5c688a',
    accent: '#f2d34e',
    shape: 'fish',
    glow: true,
  },

  // Two of Us - the sibling set. Each is deliberately half of something.
  'ss-left-boot': { art: 'object', body: '#8a5636', accent: '#4f2f1c', object: 'boot' },
  'ss-right-boot': { art: 'object', body: '#8a5636', accent: '#4f2f1c', object: 'boot' },
  'ss-map-half-a': { art: 'object', body: '#e8dcb8', accent: '#8a6a3c', object: 'map-left' },
  'ss-map-half-b': { art: 'object', body: '#e8dcb8', accent: '#8a6a3c', object: 'map-right' },
  'ss-lantern': { art: 'object', body: '#c8a94e', accent: '#5c4a1c', object: 'lantern' },
  'ss-key': { art: 'object', body: '#d8c05a', accent: '#7a6318', object: 'key', glow: true },

  // The Family Cabinet - places, not creatures.
  'fc-treehouse': { art: 'object', body: '#8a6a42', accent: '#4f7d3a', object: 'treehouse' },
  'fc-campfire': { art: 'object', body: '#e07a2a', accent: '#7a3c14', object: 'campfire' },
  'fc-rockpool': { art: 'object', body: '#5aa8b5', accent: '#2f5f68', object: 'rockpool' },
  'fc-observatory': {
    art: 'object',
    body: '#4a5578',
    accent: '#f2d34e',
    object: 'observatory',
    glow: true,
  },
};

const FALLBACK: Spec = { art: 'star', body: '#c9ccd1', accent: '#6b7280', object: 'note' };

export function hasArtFor(key: string): boolean {
  return key in SPECS;
}

interface CollectibleProps {
  collectibleKey: string;
  size?: number;
  title?: string;
  /** Silhouette for something not found yet: the shape, none of the detail. */
  hidden?: boolean;
}

export function Collectible({ collectibleKey, size = 88, title, hidden }: CollectibleProps) {
  const painted = PAINTED_BY_KEY[collectibleKey];

  if (painted) {
    return (
      <img
        className="collectible-art"
        src={painted}
        width={size}
        height={size}
        alt={title ?? (hidden ? 'Not found yet' : collectibleKey)}
        // A silhouette of a painting is done in CSS: pure black at full
        // contrast keeps the outline and destroys every colour and the eyes,
        // which is the same promise the drawn silhouette makes.
        data-hidden={hidden ? 'true' : 'false'}
        loading="lazy"
        decoding="async"
      />
    );
  }

  const spec = SPECS[collectibleKey] ?? FALLBACK;
  const id = collectibleKey.replace(/[^a-z0-9]/gi, '');

  // A silhouette keeps the OUTLINE and drops the colour, so a child can see the
  // shape of what is missing without being told what it is. The wanting is the
  // whole point of collecting.
  const shown: Spec = hidden
    ? {
        ...spec,
        body: 'var(--silhouette, #cfc7bb)',
        belly: 'var(--silhouette, #cfc7bb)',
        accent: 'var(--silhouette, #cfc7bb)',
        glow: false,
      }
    : spec;

  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-label={title ?? (hidden ? 'Not found yet' : collectibleKey)}
    >
      {shown.glow && (
        <>
          <defs>
            <radialGradient id={`glow-${id}`}>
              <stop offset="0%" stopColor={shown.accent} stopOpacity="0.55" />
              <stop offset="100%" stopColor={shown.accent} stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="48" fill={`url(#glow-${id})`} />
        </>
      )}
      {render(shown, hidden ?? false)}
    </svg>
  );
}

function render(s: Spec, hidden: boolean) {
  switch (s.art) {
    case 'critter':
      return <Critter s={s} hidden={hidden} />;
    case 'bird':
      return <Bird s={s} hidden={hidden} />;
    case 'dino':
      return <Dino s={s} hidden={hidden} />;
    case 'sea':
      return <Sea s={s} hidden={hidden} />;
    case 'star':
      return <StarPopper s={s} hidden={hidden} />;
    default:
      return <ObjectArt s={s} hidden={hidden} />;
  }
}

/** Eyes, done once. Consistent eyes are most of what makes a set feel like one. */
function Eyes({
  cx = 50,
  cy = 44,
  gap = 9,
  r = 3.4,
  hidden,
}: {
  cx?: number;
  cy?: number;
  gap?: number;
  r?: number;
  hidden: boolean;
}) {
  if (hidden) return null;
  return (
    <>
      <circle cx={cx - gap} cy={cy} r={r} fill={INK} />
      <circle cx={cx + gap} cy={cy} r={r} fill={INK} />
      <circle cx={cx - gap + 1.2} cy={cy - 1.2} r={r / 3} fill="#fff" />
      <circle cx={cx + gap + 1.2} cy={cy - 1.2} r={r / 3} fill="#fff" />
    </>
  );
}

const line = { stroke: INK, strokeWidth: 2.5, strokeLinecap: 'round' as const, fill: 'none' };

// --- archetypes ------------------------------------------------------------

function Critter({ s, hidden }: { s: Spec; hidden: boolean }) {
  return (
    <>
      {s.tail === 'bushy' && (
        <ellipse cx="24" cy="62" rx="12" ry="8" fill={s.body} stroke={INK} strokeWidth="2" />
      )}
      {s.tail === 'thin' && <path d="M28 64 q-12 4 -14 -6" {...line} />}
      {s.tail === 'flat' && (
        <ellipse cx="24" cy="64" rx="13" ry="6" fill={s.accent} stroke={INK} strokeWidth="2" />
      )}

      {/* quills */}
      {Array.from({ length: s.spikes ?? 0 }, (_, i) => (
        <path
          key={i}
          d={`M${36 + i * 5} 34 l${-2 + i * 0.4} -12`}
          stroke={s.accent}
          strokeWidth="2.6"
          strokeLinecap="round"
        />
      ))}

      <ellipse cx="54" cy="58" rx="26" ry="23" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <ellipse
        cx="56"
        cy="64"
        rx="16"
        ry="13"
        fill={s.belly ?? s.body}
        opacity={hidden ? 1 : 0.9}
      />

      {Array.from({ length: s.stripes ?? 0 }, (_, i) => (
        <path
          key={i}
          d={`M${44 + i * 7} 44 q3 12 0 24`}
          stroke={s.accent}
          strokeWidth="2"
          fill="none"
          opacity="0.8"
        />
      ))}

      {s.ears === 'round' && (
        <>
          <circle cx="40" cy="30" r="8" fill={s.body} stroke={INK} strokeWidth="2.5" />
          <circle cx="62" cy="29" r="8" fill={s.body} stroke={INK} strokeWidth="2.5" />
        </>
      )}
      {s.ears === 'pointy' && (
        <>
          <path
            d="M40 34 L36 18 L50 28 Z"
            fill={s.body}
            stroke={INK}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
          <path
            d="M62 33 L68 18 L72 32 Z"
            fill={s.body}
            stroke={INK}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </>
      )}
      {s.ears === 'tiny' && (
        <circle cx="42" cy="32" r="4" fill={s.body} stroke={INK} strokeWidth="2" />
      )}

      <circle cx="51" cy="42" r="19" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <Eyes cx={51} cy={40} hidden={hidden} />
      {!hidden && (
        <>
          <ellipse cx="51" cy="50" rx="4" ry="3" fill={INK} />
          <path d="M47 55 q4 4 8 0" {...line} strokeWidth="2" />
        </>
      )}
    </>
  );
}

function Bird({ s, hidden }: { s: Spec; hidden: boolean }) {
  const headY = s.neck === 'long' ? 26 : 36;
  return (
    <>
      <ellipse cx="52" cy="68" rx="22" ry="19" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <path d={`M52 ${headY + 12} L52 58`} stroke={s.body} strokeWidth="13" strokeLinecap="round" />
      <path
        d={`M52 ${headY + 12} L52 58`}
        stroke={INK}
        strokeWidth="2.5"
        fill="none"
        opacity="0.25"
      />

      {s.crest && (
        <path
          d={`M44 ${headY - 12} q8 -14 16 0 q-8 -4 -16 0`}
          fill={s.accent}
          stroke={INK}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      )}

      <circle cx="52" cy={headY} r="14" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <path
        d={`M64 ${headY + 1} l12 4 l-12 5 z`}
        fill={s.accent}
        stroke={INK}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <Eyes cx={50} cy={headY - 2} gap={6} r={3} hidden={hidden} />

      <path d="M36 74 q10 8 22 4" stroke={s.accent} strokeWidth="2.5" fill="none" opacity="0.8" />
      <path d="M44 87 l0 8 M60 87 l0 8" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    </>
  );
}

function Dino({ s, hidden }: { s: Spec; hidden: boolean }) {
  const headY = s.neck === 'long' ? 24 : 40;
  return (
    <>
      <path
        d="M26 70 q-14 -2 -16 -14 q10 4 16 6 z"
        fill={s.body}
        stroke={INK}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {s.wings && (
        <path
          d="M32 50 q-18 -14 -24 2 q14 2 24 8 z"
          fill={s.accent}
          stroke={INK}
          strokeWidth="2.5"
          strokeLinejoin="round"
          opacity="0.95"
        />
      )}

      {Array.from({ length: s.spikes ?? 0 }, (_, i) => (
        <path
          key={i}
          d={`M${38 + i * 9} 46 l4 -11 l4 11 z`}
          fill={s.accent}
          stroke={INK}
          strokeWidth="2"
          strokeLinejoin="round"
        />
      ))}

      <ellipse cx="52" cy="66" rx="26" ry="21" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <ellipse cx="54" cy="72" rx="16" ry="12" fill={s.belly ?? s.body} opacity="0.9" />
      <path d={`M58 ${headY + 12} L56 58`} stroke={s.body} strokeWidth="15" strokeLinecap="round" />

      <ellipse cx="60" cy={headY} rx="17" ry="14" fill={s.body} stroke={INK} strokeWidth="2.5" />
      <Eyes cx={60} cy={headY - 3} gap={7} r={3.2} hidden={hidden} />
      {!hidden && <path d={`M50 ${headY + 6} q10 5 20 1`} {...line} strokeWidth="2" />}
      <path d="M42 85 l0 8 M64 85 l0 8" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    </>
  );
}

function Sea({ s, hidden }: { s: Spec; hidden: boolean }) {
  if (s.shape === 'shell') {
    return (
      <>
        <ellipse
          cx="34"
          cy="60"
          rx="10"
          ry="8"
          fill={s.belly ?? s.body}
          stroke={INK}
          strokeWidth="2.5"
        />
        <Eyes cx={31} cy={58} gap={4} r={2.4} hidden={hidden} />
        <ellipse cx="56" cy="56" rx="28" ry="23" fill={s.body} stroke={INK} strokeWidth="2.5" />
        <path d="M40 56 h32 M56 34 v44" stroke={s.accent} strokeWidth="2.5" opacity="0.85" />
        <ellipse
          cx="56"
          cy="56"
          rx="15"
          ry="12"
          fill="none"
          stroke={s.accent}
          strokeWidth="2.5"
          opacity="0.85"
        />
        <path d="M40 76 l-4 10 M72 76 l4 10" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
      </>
    );
  }

  if (s.shape === 'blob') {
    return (
      <>
        <ellipse cx="50" cy="42" rx="24" ry="22" fill={s.body} stroke={INK} strokeWidth="2.5" />
        <Eyes cx={50} cy={40} gap={8} hidden={hidden} />
        {[0, 1, 2, 3, 4].map((i) => (
          <path
            key={i}
            d={`M${32 + i * 9} 60 q${i % 2 ? 6 : -6} 16 ${i % 2 ? 2 : -2} 26`}
            stroke={s.body}
            strokeWidth="7"
            strokeLinecap="round"
            fill="none"
          />
        ))}
      </>
    );
  }

  if (s.shape === 'flat') {
    return (
      <>
        <path
          d="M50 30 Q90 52 50 74 Q10 52 50 30 Z"
          fill={s.body}
          stroke={INK}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path d="M50 74 q2 14 -4 20" {...line} />
        <Eyes cx={50} cy={44} gap={9} r={3} hidden={hidden} />
      </>
    );
  }

  if (s.shape === 'long') {
    return (
      <>
        <path
          d="M56 26 q14 10 6 24 q-8 14 -2 24 q4 8 -6 12"
          stroke={s.body}
          strokeWidth="15"
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="56" cy="28" r="13" fill={s.body} stroke={INK} strokeWidth="2.5" />
        <path
          d="M68 28 l10 3 l-10 4 z"
          fill={s.accent}
          stroke={INK}
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <Eyes cx={54} cy={26} gap={5} r={2.8} hidden={hidden} />
      </>
    );
  }

  const huge = s.shape === 'huge';
  return (
    <>
      <path
        d={huge ? 'M18 56 q10 -10 20 -4 z' : 'M20 52 l-10 -10 l0 24 z'}
        fill={s.accent}
        stroke={INK}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <ellipse
        cx="54"
        cy="54"
        rx={huge ? 32 : 27}
        ry={huge ? 20 : 18}
        fill={s.body}
        stroke={INK}
        strokeWidth="2.5"
      />
      <path
        d={`M54 ${huge ? 34 : 36} l0 -12 l12 10 z`}
        fill={s.accent}
        stroke={INK}
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <ellipse cx="58" cy="60" rx="18" ry="10" fill={s.belly ?? s.body} opacity="0.9" />
      {Array.from({ length: s.stripes ?? 0 }, (_, i) => (
        <path
          key={i}
          d={`M${46 + i * 11} 38 q-3 16 0 32`}
          stroke={s.accent}
          strokeWidth="3.5"
          fill="none"
          opacity="0.9"
        />
      ))}
      <Eyes cx={70} cy={50} gap={0} r={3.2} hidden={hidden} />
      {!hidden && <path d="M74 58 q-6 4 -12 1" {...line} strokeWidth="2" />}
    </>
  );
}

function StarPopper({ s, hidden }: { s: Spec; hidden: boolean }) {
  const points = starPoints(50, 48, 34, 16, 5);
  return (
    <>
      <polygon
        points={points}
        fill={s.body}
        stroke={INK}
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <Eyes cx={50} cy={44} gap={8} r={3.2} hidden={hidden} />
      {!hidden && (
        <>
          <path d="M44 54 q6 6 12 0" {...line} strokeWidth="2" />
          {/* A quaver, because these are the Star Poppers. */}
          <path d="M60 60 v14" stroke={s.accent} strokeWidth="2.6" strokeLinecap="round" />
          <circle cx="57" cy="75" r="4" fill={s.accent} />
          <path
            d="M60 60 q7 3 6 9"
            stroke={s.accent}
            strokeWidth="2.6"
            fill="none"
            strokeLinecap="round"
          />
        </>
      )}
    </>
  );
}

function starPoints(cx: number, cy: number, outer: number, inner: number, n: number): string {
  const pts: string[] = [];
  for (let i = 0; i < n * 2; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (Math.PI / n) * i - Math.PI / 2;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`);
  }
  return pts.join(' ');
}

function ObjectArt({ s, hidden }: { s: Spec; hidden: boolean }) {
  const stroke = { stroke: INK, strokeWidth: 2.5, strokeLinejoin: 'round' as const };

  switch (s.object) {
    case 'boot':
      return (
        <>
          <path d="M36 22 h16 v34 q0 8 10 12 l14 6 q6 3 6 9 v6 H36 z" fill={s.body} {...stroke} />
          <path d="M36 76 h46" stroke={s.accent} strokeWidth="5" strokeLinecap="round" />
          {!hidden && (
            <path
              d="M40 34 h12 M40 44 h12"
              stroke={s.accent}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
          )}
        </>
      );
    case 'egg':
      return (
        <>
          <path
            d="M50 16 q22 18 22 40 a22 24 0 0 1 -44 0 q0 -22 22 -40 z"
            fill={s.body}
            {...stroke}
          />
          {!hidden && (
            <>
              <path
                d="M32 52 l8 -6 l7 7 l8 -8 l7 7 l6 -5"
                stroke={s.accent}
                strokeWidth="2.5"
                fill="none"
              />
              <circle cx="42" cy="66" r="3" fill={s.accent} opacity="0.6" />
            </>
          )}
        </>
      );
    case 'map-left':
    case 'map-right': {
      const left = s.object === 'map-left';
      return (
        <>
          <path
            d={left ? 'M18 22 l30 4 v52 l-30 -4 z' : 'M82 22 l-30 4 v52 l30 -4 z'}
            fill={s.body}
            {...stroke}
          />
          {!hidden && (
            <>
              <path
                d={left ? 'M26 40 q10 6 18 2' : 'M74 40 q-10 6 -18 2'}
                stroke={s.accent}
                strokeWidth="2.5"
                fill="none"
              />
              <path
                d={left ? 'M28 58 l6 -6 l6 6' : 'M72 58 l-6 -6 l-6 6'}
                stroke={s.accent}
                strokeWidth="2.5"
                fill="none"
              />
              {!left && (
                <path
                  d="M62 66 l6 6 M68 66 l-6 6"
                  stroke="#b0432a"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              )}
            </>
          )}
        </>
      );
    }
    case 'lantern':
      return (
        <>
          <path d="M42 20 h16 v6 h-16 z" fill={s.accent} {...stroke} />
          <path d="M38 26 h24 v40 h-24 z" fill={s.body} {...stroke} />
          <path d="M34 66 h32 v8 h-32 z" fill={s.accent} {...stroke} />
          {!hidden && (
            <path
              d="M50 36 q7 8 0 20 q-7 -12 0 -20 z"
              fill="#f6e7a8"
              stroke={INK}
              strokeWidth="1.5"
            />
          )}
          <path d="M50 20 v-8" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
        </>
      );
    case 'key':
      return (
        <>
          <circle cx="34" cy="42" r="16" fill="none" stroke={s.body} strokeWidth="9" />
          <circle cx="34" cy="42" r="16" fill="none" stroke={INK} strokeWidth="2" opacity="0.35" />
          <path d="M46 50 L76 78" stroke={s.body} strokeWidth="9" strokeLinecap="round" />
          <path
            d="M64 66 l8 -8 M70 72 l8 -8"
            stroke={s.body}
            strokeWidth="7"
            strokeLinecap="round"
          />
          {!hidden && (
            <circle cx="34" cy="42" r="6" fill="none" stroke={s.accent} strokeWidth="2.5" />
          )}
        </>
      );
    case 'treehouse':
      return (
        <>
          <path d="M46 60 h8 v32 h-8 z" fill={s.body} {...stroke} />
          <ellipse cx="50" cy="40" rx="30" ry="22" fill={s.accent} {...stroke} />
          <path d="M34 46 h32 v20 h-32 z" fill={s.body} {...stroke} />
          <path d="M32 46 l18 -14 l18 14 z" fill={s.body} {...stroke} />
          {!hidden && <rect x="45" y="54" width="10" height="12" fill={INK} opacity="0.5" />}
        </>
      );
    case 'campfire':
      return (
        <>
          <path
            d="M26 76 l48 -8 M26 68 l48 8"
            stroke="#7a5a34"
            strokeWidth="7"
            strokeLinecap="round"
          />
          <path
            d="M50 22 q16 18 10 30 q-4 8 -10 8 q-6 0 -10 -8 q-6 -12 10 -30 z"
            fill={s.body}
            {...stroke}
          />
          {!hidden && (
            <path d="M50 40 q7 9 3 15 q-3 4 -6 0 q-4 -7 3 -15 z" fill="#f6e7a8" stroke="none" />
          )}
        </>
      );
    case 'rockpool':
      return (
        <>
          <ellipse cx="50" cy="60" rx="34" ry="24" fill={s.body} {...stroke} />
          <ellipse cx="50" cy="60" rx="22" ry="14" fill={s.accent} opacity="0.5" />
          {!hidden && (
            <>
              <path
                d="M32 60 q8 -6 16 0 q8 6 16 0"
                stroke="#f6f2ea"
                strokeWidth="2.5"
                fill="none"
                opacity="0.9"
              />
              <path d="M62 48 l4 -6 l4 6 l-4 3 z" fill="#e46a8a" stroke={INK} strokeWidth="1.5" />
            </>
          )}
        </>
      );
    case 'observatory':
      return (
        <>
          <path d="M28 88 h44 v-6 h-44 z" fill={s.body} {...stroke} />
          <path d="M34 82 h32 v-22 h-32 z" fill={s.body} {...stroke} />
          <path d="M32 60 a18 18 0 0 1 36 0 z" fill={s.body} {...stroke} />
          <path d="M50 44 L74 26" stroke={s.accent} strokeWidth="6" strokeLinecap="round" />
          {!hidden && (
            <>
              <polygon points={starPoints(22, 24, 8, 3.5, 5)} fill={s.accent} />
              <circle cx="78" cy="46" r="2.5" fill={s.accent} />
            </>
          )}
        </>
      );
    default:
      return <polygon points={starPoints(50, 50, 32, 15, 5)} fill={s.body} {...stroke} />;
  }
}
